(ns ui.view.transaction-process.sub
  (:require
   [common.number-utils :as nu]
   [common.schema.calculated-field :as calc-field]
   [common.schema.registry :as registry]
   [common.transaction-process.config :as tp-config]
   [common.transaction-process.permissions :as tp-permissions]
   [common.transaction-process.resolver :as resolver]
   [common.transaction-process.templates.registry :as template-registry]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [reagent.ratom :as ratom]
   [ui.state.session.sub :as session-sub]
   [ui.view.transaction-process.model :as model]
   [ui.view.transaction-process.util :as tp-util]))

(rf/reg-sub
 ::state
 (fn [db [_ {:keys [state-path] :as _ctx}]]
   (get-in db state-path)))

(rf/reg-sub
 ::initialized?
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/initialized-path)))

(rf/reg-sub
 ::server-state
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/server-state-path)))

(rf/reg-sub
 ::not-found?
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (boolean (get-in state model/not-found-path))))

(rf/reg-sub
 ::draft
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/draft-path)))

(rf/reg-sub
 ::saving?
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/saving-path)))

(rf/reg-sub-raw
 ;; Session default currency as a reactive input signal, so the pure subs below
 ;; can use it without derefing the session cursor directly in their computation.
 ::default-currency
 (fn [_ _]
   (ratom/reaction @session/default-currency)))

(defn- calculated-field-schemas
  [template]
  (->> (keys (:template/fields template))
       (keep (fn [attr]
               (let [fs (registry/get-field-schema nil attr)]
                 (when (:field/calculated-field fs)
                   [attr fs]))))
       vec))

(defn- resolve-calculated-fields
  "Evaluate the template fields that are schema `calculated-field`s and merge
   their values — plus, for currency-value fields, their computed currency
   companion (`--sym`) — into `entity`. Pure: the TP calc-fns depend only on this
   entity's own fields and read their definition from the base schema; the sole
   external input is `default-currency`, so a filled amount whose `--sym` is still
   unset counts as the default currency. `nu/bigdec->float` normalises the values
   (currency amounts arrive from the server as transit bigdecs, not plain JS
   numbers). Per-field calc errors are swallowed by `calculated-field/calculate`."
  [entity field-schemas default-currency]
  (reduce
   (fn [m [attr fs]]
     (let [calc (:field/calculated-field fs)
           m    (assoc m attr (calc-field/calculate
                               {:calc-fn          calc
                                :entity-data      m
                                :default-currency default-currency
                                :bigdec->float    nu/bigdec->float
                                :float->bigdec    nu/float->bigdec}))]
       (if (not= :data.type/currency-value (:field/data-type fs))
         m
         (let [sym-kw   (cu/currency->symbol attr)
               sym-calc (:field/calculated-field (registry/get-field-schema nil sym-kw))]
           (cond-> m
             sym-calc (assoc sym-kw (calc-field/calculate
                                     {:calc-fn          sym-calc
                                      :entity-data      m
                                      :default-currency default-currency
                                      :bigdec->float    nu/bigdec->float
                                      :float->bigdec    nu/float->bigdec})))))))
   entity
   field-schemas))

(rf/reg-sub
 ::calculated-field-schemas
 (fn [[_ ctx]]
   [(rf/subscribe [::template ctx])])
 (fn [[template]]
   (calculated-field-schemas template)))

(rf/reg-sub
 ::process
 (fn [[_ ctx]]
   [(rf/subscribe [::server-state ctx])
    (rf/subscribe [::draft ctx])
    (rf/subscribe [::calculated-field-schemas ctx])
    (rf/subscribe [::default-currency])])
 (fn [[server draft calculated-field-schemas default-currency]]
   (when server
     (-> (merge server draft)
         (resolve-calculated-fields calculated-field-schemas default-currency)))))

(rf/reg-sub
 ::dirty?
 ;; A draft entry counts as "unsaved" only if the user has not already sent
 ;; that value to the backend. Once a save is acked (in-flight :ok?), entries
 ;; that still match what we sent are in the post-ack/pre-push gap, not truly
 ;; unsaved — the backend has them. This keeps the indicator and Save button
 ;; from flickering during that gap, and hides the stuck-draft state that
 ;; results from a backend no-op (same-value save that doesn't bump
 ;; :common/updated-on).
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (let [draft (get-in state model/draft-path)
         {:keys [fields ok?]} (get-in state model/in-flight-path)]
     (if (and ok? (seq fields))
       ;; An entry is unsaved iff its key isn't covered by the acked snapshot
       ;; or the draft value differs from what we sent. contains? matters
       ;; separately from get so that `{:x nil}` in draft (user cleared a
       ;; field) is flagged even when `fields` has no :x.
       (boolean (some (fn [[k v]]
                        (or (not (contains? fields k))
                            (not= v (get fields k))))
                      draft))
       (boolean (seq draft))))))

(rf/reg-sub
 ::template
 (fn [[_ ctx]]
   [(rf/subscribe [::server-state ctx])])
 (fn [[server]]
   (when-let [template-kw (:transaction-process/template-kw server)]
     (template-registry/get-template template-kw))))

(rf/reg-sub
 ::current-stage
 (fn [[_ ctx]]
   [(rf/subscribe [::server-state ctx])
    (rf/subscribe [::draft ctx])])
 (fn [[server draft]]
   (or (:transaction-process/stage draft)
       (:transaction-process/stage server))))

(rf/reg-sub
 ::stage-config
 (fn [[_ ctx]]
   [(rf/subscribe [::template ctx])
    (rf/subscribe [::current-stage ctx])])
 (fn [[template current-stage]]
   (when template
     (template-registry/get-stage template current-stage))))

(rf/reg-sub
 ::stages
 (fn [[_ ctx]]
   [(rf/subscribe [::template ctx])])
 (fn [[template]]
   (:template/stages template)))

(rf/reg-sub
 ::transitions
 (fn [[_ ctx]]
   [(rf/subscribe [::template ctx])])
 (fn [[template]]
   (:template/transitions template)))

(rf/reg-sub
 ::team-assignments
 (fn [[_ ctx]]
   [(rf/subscribe [::process ctx])])
 (fn [[process]]
   (tp-util/get-team-assignments process)))

;; UI state

(rf/reg-sub
 ::show-permissions?
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/show-permissions-path)))

(rf/reg-sub
 ::show-details?
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/show-details-path)))

(rf/reg-sub
 ::sidebar-collapsed?
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/sidebar-collapsed-path)))

(rf/reg-sub
 ::detail-panel
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/detail-panel-path)))

(rf/reg-sub
 ::selected-verification-slot-kw
 (fn [[_ ctx]]
   [(rf/subscribe [::detail-panel ctx])])
 (fn [[panel]]
   (when (= :verification (:panel-type panel))
     (get-in panel [:item :slot-kw]))))

(rf/reg-sub
 ::modal
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/modal-path)))

(rf/reg-sub
 ::skip-mandatory-validation?
 (fn [db]
   (get-in db model/skip-mandatory-validation-path false)))

(rf/reg-sub
 ::impersonation
 (fn [db]
   (get-in db model/impersonation-path)))

;; Effective read-only: the dev impersonation's global-role wins when active,
;; otherwise the real session read-only flag. Used by the action bar + header so
;; impersonating a read-only user disables save/forward like the real thing.
(rf/reg-sub-raw
 ::effective-read-only?
 (fn [_ _]
   (let [imp-sub (rf/subscribe [::impersonation])
         ro-sub  (rf/subscribe [::session-sub/read-only-user?])]
     (ratom/reaction
      (let [imp @imp-sub]
        (if (:active? imp)
          (= :read-only (:global-role imp))
          @ro-sub))))))

;; Business logic subs

(defn- can-write-field-reaction
  [ctx {:keys [field-kw ignore-terminal-lock? terminal-only?]}]
  (let [template-sub (rf/subscribe [::template ctx])
        team-sub (rf/subscribe [::team-assignments ctx])
        stage-sub (rf/subscribe [::current-stage ctx])
        process-sub (rf/subscribe [::process ctx])
        imp-sub (rf/subscribe [::impersonation])]
    (ratom/reaction
     (let [template @template-sub
           team-assignments @team-sub
           current-stage @stage-sub
           process @process-sub
           terminal? (model/terminal? process)
           perm-inputs (model/impersonation-perm-inputs
                        {:impersonation    @imp-sub
                         :account-id       @session/account-id
                         :team-assignments team-assignments
                         :read-only-user?  (session/is-read-only-user?)
                         :admin?           (session/is-admin?)})]
       (and (or (not terminal-only?) terminal?)
            (tp-permissions/can-write-field?
             (merge {:template template
                     :field-kw field-kw
                     :stage-id current-stage
                     :locked? (and terminal? (not ignore-terminal-lock?))}
                    perm-inputs)))))))

(rf/reg-sub-raw
 ::can-write-field?
 (fn [_ [_ ctx field-kw]]
   (can-write-field-reaction ctx {:field-kw field-kw})))

(rf/reg-sub-raw
 ::can-reopen?
 (fn [_ [_ ctx]]
   (can-write-field-reaction
    ctx
    {:field-kw :transaction-process/status
     :ignore-terminal-lock? true
     :terminal-only? true})))

(rf/reg-sub-raw
 ::visible-fields
 (fn [_ [_ ctx card-fields]]
   (let [process-sub (rf/subscribe [::process ctx])
         template-sub (rf/subscribe [::template ctx])
         team-sub (rf/subscribe [::team-assignments ctx])
         stage-sub (rf/subscribe [::current-stage ctx])
         imp-sub (rf/subscribe [::impersonation])]
     (ratom/reaction
      (let [process @process-sub
            template @template-sub
            team-assignments @team-sub
            current-stage @stage-sub
            perm-inputs (model/impersonation-perm-inputs
                         {:impersonation    @imp-sub
                          :account-id       @session/account-id
                          :team-assignments team-assignments
                          :read-only-user?  (session/is-read-only-user?)
                          :admin?           (session/is-admin?)})
            fields-config (:template/fields template)
            value-lookup process]
        (when (and process template)
          (->> card-fields
               (keep (fn [fkw]
                       (let [fc (get fields-config fkw)
                             attachment-slot? (tp-util/attachment-slot-field? fc)
                             calculated? (and (not attachment-slot?)
                                              (some? (:field/calculated-field
                                                      (registry/get-field-schema nil fkw))))
                             perm (let [p (tp-permissions/resolve-field-permission
                                           (merge {:template  template
                                                   :field-kw  fkw
                                                   :stage-id  current-stage
                                                   :locked?   (model/terminal? process)}
                                                  perm-inputs))]
                                    ;; Calculated fields are system-computed and have no
                                    ;; stored attribute — never writable in the form,
                                    ;; regardless of the admin / no-team-yet :w policies.
                                    (if (and calculated? (= :w p)) :r p))
                             visible? (tp-util/field-visible?
                                       {:field-config fc
                                        :value-lookup value-lookup})]
                         (when (and (not= perm :none) visible?)
                           {:field-kw     fkw
                            :field-config fc
                            :permission   perm
                            :calculated?  calculated?
                            :mandatory?   (resolver/field-mandatory? {:entry    fc
                                                                      :visible? visible?
                                                                      :lookup   value-lookup})}))))
               vec)))))))

(rf/reg-sub-raw
 ::tp-with-resolved-slots
 ;; The TP process entity with every dictionary entity-slot resolved to its child
 ;; entity map (named single-ref, resolved through the session — hence reg-sub-raw).
 ;; The resolver (`slots-by-category`, `incomplete-required-slots`) reads child
 ;; maps straight off the slot attributes, so this is the one place that bridges
 ;; the named refs to the session.
 (fn [_ [_ ctx]]
   (let [process-sub  (rf/subscribe [::process ctx])
         template-sub (rf/subscribe [::template ctx])]
     (ratom/reaction
      (let [process  @process-sub
            template @template-sub]
        (when (and process template)
          (tp-util/tp-with-resolved-slots (:template/dictionary template) process)))))))

(rf/reg-sub-raw
 ::summary-groups
 ;; Render-ready sidebar summary: [{:group label :fields [{:field-kw :label :value}]}].
 ;; :template/summary-fields carries only grouped field-kws; label + value (incl.
 ;; refs / currency / enum / Yes-No labels) resolve from the template field-config
 ;; (preferred) or the tenant schema here, so the card draws with no per-field
 ;; metadata. reg-sub-raw because value materialization needs session-backed
 ;; tenant/schema/entity lookup signals.
 (fn [_ [_ ctx]]
   (let [process-sub  (rf/subscribe [::process ctx])
         template-sub (rf/subscribe [::template ctx])
         entities-sub (rf/subscribe [::session-sub/legacy-entities])]
     (ratom/reaction
      (let [process       @process-sub
            template      @template-sub
            id->entity    @entities-sub
            field-configs (:template/fields template)]
        (when (and process template)
          (->> (:template/summary-fields template)
               (keep (fn [{:summary/keys [group fields]}]
                       (let [visible-fields (->> fields
                                                 (filter (fn [field-kw]
                                                           (let [field-config (get field-configs field-kw)]
                                                             (tp-util/field-visible?
                                                              {:field-config field-config
                                                               :value-lookup process}))))
                                                 (mapv (fn [field-kw]
                                                         (tp-util/summary-field-display
                                                          {:field-kw     field-kw
                                                           :entity       process
                                                           :field-configs field-configs
                                                           :id->entity   id->entity}))))]
                         (when (seq visible-fields)
                           {:group  group
                            :fields visible-fields})))))))))))

(defn- template+tp-signals
  [[_ ctx]]
  [(rf/subscribe [::template ctx])
   (rf/subscribe [::tp-with-resolved-slots ctx])])

(rf/reg-sub
 ::matched-decisions
 template+tp-signals
 (fn [[template tp]]
   (when (and template tp)
     (resolver/slots-by-category (:template/dictionary template) tp :decisions))))

(rf/reg-sub
 ::matched-attestations
 template+tp-signals
 (fn [[template tp]]
   (when (and template tp)
     (resolver/slots-by-category (:template/dictionary template) tp :attestations))))

(rf/reg-sub
 ::matched-declarations
 template+tp-signals
 (fn [[template tp]]
   (when (and template tp)
     (resolver/slots-by-category (:template/dictionary template) tp :declarations))))

(rf/reg-sub
 ::incomplete-required-sub-entities
 ;; Delegates to the pure cljc resolver over the dictionary — one walk for all
 ;; three sub-entity categories (decisions/attestations/declarations). Per-slot
 ;; visibility (Lux fund, DD-type linkage) is slot visibility, evaluated inside
 ;; the engine. The TP carries its
 ;; slot refs resolved to child maps; lookup is the process attrs (Lux-ness lives
 ;; on the TP as :transaction-process/lux-fund?).
 (fn [[_ ctx]]
   [(rf/subscribe [::template ctx])
    (rf/subscribe [::tp-with-resolved-slots ctx])
    (rf/subscribe [::process ctx])])
 (fn [[template tp process]]
   (when (and template tp)
     (resolver/incomplete-required-slots
      {:dictionary (:template/dictionary template)
       :tp         tp
       :lookup     process}))))

(rf/reg-sub
 ::blocking-sub-entities-for-transition
 (fn [[_ ctx _transition]]
   [(rf/subscribe [::incomplete-required-sub-entities ctx])
    (rf/subscribe [::template ctx])
    (rf/subscribe [::stages ctx])])
 (fn [[incomplete template stages] [_ _ctx transition]]
   (resolver/blocking-for-transition
    {:incomplete  incomplete
     :transition  transition
     :stage-ids   (mapv :stage/id stages)
     :stage-gates (:template/stage-gates template)})))

(rf/reg-sub-raw
 ::default-decision-participant-ids
 (fn [_ [_ ctx]]
   (let [process-sub (rf/subscribe [::process ctx])
         template-sub (rf/subscribe [::template ctx])]
     (ratom/reaction
      (let [process @process-sub
            template @template-sub
            vehicle-id (:transaction-process/vehicle process)
            fund (when vehicle-id
                   (cu/find-first-by :db/id vehicle-id @session/funds))
            vehicle-config (when fund
                             (tp-config/parse-vehicle-config
                              (:fund/transaction-process-config fund)))
            template-kw (:template/id template)]
        (get-in vehicle-config [:tp-config/default-decision-participants template-kw] {}))))))
